export type Oth360InviteMode = 'SELF' | 'EMPLOYEE';

export type Oth360InviteFormRef = {
  id: string;
  /**
   * Some backends may omit version for public auth responses.
   * Keep it optional so we can still use schemaJson when present.
   */
  version?: number;
  schemaJson?: unknown;
};

export type Oth360InviteContext = {
  mode: Oth360InviteMode;
  subjectName: string;
  /**
   * Server flag indicating if the response has already been submitted.
   * Used to enable/disable certain actions in the public UI.
   */
  hasSubmitted: boolean;
  /**
   * If null, the frontend should fall back to the standard form.
   * If present, render using the server-provided schemaJson.
   */
  form: Oth360InviteFormRef | null;
  employeeLink: string | null;
  /**
   * Optional ID that maps to backend evaluation round resource.
   * Needed for AI summary endpoints that are keyed by evaluation round id.
   */
  evaluationRoundId?: string | null;
};

const STORAGE_KEY_V2 = 'oth360InviteContext.v2';
const STORAGE_KEY_V1 = 'oth360InviteContext.v1';

export function saveOth360InviteContext(ctx: Oth360InviteContext) {
  try {
    localStorage.setItem(STORAGE_KEY_V2, JSON.stringify(ctx));
  } catch {
    // ignore (storage may be unavailable)
  }
}

export function loadOth360InviteContext(): Oth360InviteContext | null {
  try {
    const rawV2 = localStorage.getItem(STORAGE_KEY_V2);
    if (rawV2) {
      const parsed = JSON.parse(rawV2) as Partial<Oth360InviteContext>;
      if (!parsed.mode || !parsed.subjectName) {
        return null;
      }
      return {
        mode: parsed.mode,
        subjectName: parsed.subjectName,
        hasSubmitted: parsed.hasSubmitted === true,
        form: parsed.form ?? null,
        employeeLink: parsed.employeeLink ?? null,
        evaluationRoundId:
          typeof parsed.evaluationRoundId === 'string'
            ? parsed.evaluationRoundId
            : null,
      };
    }

    // Backward-compatible migration from v1 → v2.
    const rawV1 = localStorage.getItem(STORAGE_KEY_V1);
    if (!rawV1) {
      return null;
    }

    const v1 = JSON.parse(rawV1) as Partial<{
      mode: Oth360InviteMode;
      subjectName: string;
      formId: string;
      formVersion: number;
      employeeLink: string | null;
    }>;

    if (
      !v1.mode ||
      !v1.subjectName ||
      !v1.formId ||
      typeof v1.formVersion !== 'number'
    ) {
      return null;
    }

    const migrated: Oth360InviteContext = {
      mode: v1.mode,
      subjectName: v1.subjectName,
      hasSubmitted: false,
      form: { id: v1.formId, version: v1.formVersion },
      employeeLink: v1.employeeLink ?? null,
      evaluationRoundId: null,
    };
    saveOth360InviteContext(migrated);
    return migrated;
  } catch {
    return null;
  }
}

export function clearOth360InviteContext() {
  try {
    localStorage.removeItem(STORAGE_KEY_V2);
    localStorage.removeItem(STORAGE_KEY_V1);
  } catch {
    // ignore
  }
}

export function getInviteModeFlags(mode: Oth360InviteMode) {
  return {
    isSelfLink: mode === 'SELF',
    isEmployeeLink: mode === 'EMPLOYEE',
  };
}
